import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import style from "./index.module.scss";
import CloseIcon from "../../shared/icons/CloseIcon";
import { Driver, File, Order } from "../../../services";
import { requestErrorDisplay } from "../../../utils";
import Loading from "../../shared/Loading/Loading";
import cn from "classnames";
import DocumentNewIcon from "../../shared/icons/DocumentNewIcon";
import EyeIcon from "../../shared/icons/EyeIcon";
import UnloadIcon from "../../shared/icons/UnloadIcon";
import PhotoViewer from "../../partners/components/PhotoViewer";
import { SquarePen } from "lucide-react";
import InputCustom from "../../shared/forms/input-custom";
import { toast } from "react-toastify";
import SelectCustom from "../../shared/forms/select-custom";

const formatDocumentType = (contentType) => {
  const mimeMap = {
    "application/pdf": "pdf",
    "image/png": "png",
    "image/jpeg": "jpg",
    "image/webp": "webp",
    "application/msword": "doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  };

  return mimeMap[contentType] || contentType?.split("/")[1];
};

const VehicleInfoModal = ({ isOpen, onClose, vehicleId, onVehicleUpdate, isEditMode, setIsEditMode }) => {
  const { t, i18n } = useTranslation();
  const [isLoading, setLoading] = useState(false);
  const [vehicleInfo, setVehicleInfo] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loadingStates, setLoadingStates] = useState({});
  const [loadingStatesDownload, setLoadingStatesDownload] = useState({});
  const [isPhotoViewerOpen, setIsPhotoViewerOpen] = useState(false);
  const [imgUrl, setImgUrl] = useState("");
  const [currentDocType, setCurrentDocType] = useState("");
  const [cachedFiles, setCachedFiles] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const [vehicleTypeId, setVehicleTypeId] = useState("");
  const [vehicleType, setVehicleType] = useState(null);
  const [carNumber, setCarNumber] = useState("");
  const [carModel, setCarModel] = useState("");
  const [trailerNumber, setTrailerNumber] = useState("");
  const [trailerModel, setTrailerModel] = useState("");
  const [loadingTypeId, setLoadingTypeId] = useState("");
  const [loadingType, setLoadingType] = useState(null);
  const [fileUpdates, setFileUpdates] = useState({});

  const [loadingTypes, setLoadingTypes] = useState([]);
  const [types, setTypes] = useState([]);

  const [errors, setErrors] = useState({
    carNumber: "",
    carModel: "",
    trailerNumber: "",
    trailerModel: "",
  });

  useEffect(() => {
    if (isOpen && vehicleId) {
      fetchVehicleInfo();
      fetchVehicleDocuments();
    } else if (!isOpen) {
      setIsEditMode(false);
      setFileUpdates({});
      setErrors({
        carNumber: "",
        carModel: "",
        trailerNumber: "",
        trailerModel: "",
      });
    }
  }, [isOpen, vehicleId]);

  useEffect(() => {
    if (isEditMode) {
      getDefaultData();
    }
  }, [isEditMode, i18n.language]);

  const getDefaultData = async () => {
    await Order.getLoadingTypesDisc(i18n.language)
      .then((response) => {
        setLoadingTypes(response.data);
      })
      .catch((error) => {
        requestErrorDisplay(error);
      });

    await Order.getTypes()
      .then((response) => {
        setTypes(response.data);
      })
      .catch((error) => {
        requestErrorDisplay(error);
      });
  };

  const fetchVehicleInfo = async () => {
    setLoading(true);
    try {
      const response = await Driver.getVehicleInfo(vehicleId);
      const data = response.data;
      setVehicleInfo(data);
      setVehicleTypeId(data.vehicleTypeId || "");
      setCarNumber(data.carNumber || "");
      setCarModel(data.carModel || "");
      setTrailerNumber(data.trailerNumber || "");
      setTrailerModel(data.trailerModel || "");
      setLoadingTypeId(data.loadingTypeId || "");

      if (data.vehicleTypeId && data.vehicleTypeName) {
        setVehicleType({ id: data.vehicleTypeId, name: data.vehicleTypeName });
      }
      if (data.loadingTypeId && data.loadingTypeName) {
        setLoadingType({ id: data.loadingTypeId, name: data.loadingTypeName });
      }
    } catch (error) {
      requestErrorDisplay(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicleDocuments = async () => {
    try {
      const response = await File.getDocListVehicle(vehicleId);
      setDocuments(response.data);
    } catch (error) {
      console.error("Error loading vehicle documents:", error);
    }
  };

  const setLoadingViewer = (docId, isLoading) => {
    setLoadingStates((prev) => ({ ...prev, [docId]: isLoading }));
  };

  const setLoadingDownload = (docId, isLoading) => {
    setLoadingStatesDownload((prev) => ({ ...prev, [docId]: isLoading }));
  };

  const openViewPhoto = (fileId) => {
    if (cachedFiles[fileId]) {
      const file = cachedFiles[fileId];
      setCurrentDocType(fileId);

      if (file.contentType === "application/pdf") {
        window.open(file.url, "_blank");
      } else {
        setImgUrl(file.url);
        setIsPhotoViewerOpen(true);
      }
      return;
    }

    setLoadingViewer(fileId, true);

    File.getDocById(fileId)
      .then((response) => {
        setLoadingViewer(fileId, false);
        const file = new Blob([response.data], {
          type: response.data.type,
        });

        const fileURL = URL.createObjectURL(file);
        setCachedFiles((prev) => ({
          ...prev,
          [fileId]: {
            blob: file,
            url: fileURL,
            type: response.data.type,
            contentType: response.data.type,
          },
        }));
        setCurrentDocType(fileId);

        if (response.data.type === "application/pdf") {
          window.open(fileURL, "_blank");
        } else {
          setImgUrl(fileURL);
          setIsPhotoViewerOpen(true);
        }
      })
      .catch((error) => {
        setLoadingViewer(fileId, false);
        console.log("Произошла ошибка, попробуйте позже", error);
        toast.error(t("common.genericError"));
      });
  };

  const onDownload = (fileId) => {
    const doc = documents.find((x) => x.fileId === fileId);
    if (!doc) {
      toast.error(t("orderInfo.driverDocumentViewer.docNotFound"));
      return;
    }

    if (cachedFiles[doc.fileId]) {
      const file = cachedFiles[doc.fileId];
      const downloadElement = document.createElement("a");
      downloadElement.href = file.url;
      downloadElement.download = `${doc.typeName}.${formatDocumentType(file.type)}`;
      downloadElement.click();
      return;
    }

    setLoadingDownload(fileId, true);
    File.getDocById(doc.fileId)
      .then((response) => {
        const fileBlob = new Blob([response.data], {
          type: response.data.type,
        });

        const fileURL = URL.createObjectURL(fileBlob);

        setCachedFiles((prev) => ({
          ...prev,
          [doc.fileId]: { blob: fileBlob, url: fileURL, type: response.data.type },
        }));

        const downloadElement = document.createElement("a");
        downloadElement.href = fileURL;
        downloadElement.download = `${doc.typeName}.${formatDocumentType(response.data.type)}`;
        downloadElement.click();
      })
      .catch((error) => {
        console.log("Произошла ошибка, попробуйте позже", error);
        toast.error(t("common.genericError"));
      })
      .finally(() => {
        setLoadingDownload(fileId, false);
      });
  };

  const handlePhotoViewerClose = () => {
    setIsPhotoViewerOpen(false);
  };

  const validateForm = () => {
    const newErrors = {
      carNumber: "",
      carModel: "",
      trailerNumber: "",
      trailerModel: "",
    };

    let isValid = true;

    if (!carNumber.trim()) {
      newErrors.carNumber = t("carsCreatePage.validation.invalidCarNumber");
      isValid = false;
    }

    if (!carModel.trim()) {
      newErrors.carModel = t("carsCreatePage.validation.invalidCarModel");
      isValid = false;
    }

    if (!trailerNumber.trim()) {
      newErrors.trailerNumber = t("carsCreatePage.validation.invalidTrailerNumber");
      isValid = false;
    }

    if (!trailerModel.trim()) {
      newErrors.trailerModel = t("carsCreatePage.validation.invalidTrailerModel");
      isValid = false;
    }

    setErrors(newErrors);

    if (!isValid) {
      Object.values(newErrors).forEach((error) => {
        if (error) {
          toast.error(error);
        }
      });
    }

    return isValid;
  };

  const handleEditToggle = () => {
    if (isEditMode) {
      setVehicleTypeId(vehicleInfo.vehicleTypeId || "");
      setCarNumber(vehicleInfo.carNumber || "");
      setCarModel(vehicleInfo.carModel || "");
      setTrailerNumber(vehicleInfo.trailerNumber || "");
      setTrailerModel(vehicleInfo.trailerModel || "");
      setLoadingTypeId(vehicleInfo.loadingTypeId || "");

      if (vehicleInfo.vehicleTypeId && vehicleInfo.vehicleTypeName) {
        setVehicleType({ id: vehicleInfo.vehicleTypeId, name: vehicleInfo.vehicleTypeName });
      }
      if (vehicleInfo.loadingTypeId && vehicleInfo.loadingTypeName) {
        setLoadingType({ id: vehicleInfo.loadingTypeId, name: vehicleInfo.loadingTypeName });
      }

      setFileUpdates({});
      setErrors({
        carNumber: "",
        carModel: "",
        trailerNumber: "",
        trailerModel: "",
      });
    }
    setIsEditMode(!isEditMode);
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      const updateData = {
        vehicleTypeId: vehicleType?.id || vehicleTypeId,
        carNumber: carNumber.trim(),
        carModel: carModel.trim(),
        trailerNumber: trailerNumber.trim(),
        trailerModel: trailerModel.trim(),
        loadingTypeId: loadingType?.id || loadingTypeId,
      };

      await Driver.updateVehicle(vehicleId, updateData);

      const uploadFile = async ({ vehicleId, side, file, type }) => {
        const formData = new FormData();
        formData.append("vehicleId", vehicleId);
        formData.append("side", side);
        formData.append("file", file);
        formData.append("type", type);
        await File.addVehicleFile(formData);
      };

      for (const [key, file] of Object.entries(fileUpdates)) {
        if (file) {
          const type = parseInt(key);
          await uploadFile({
            vehicleId,
            side: 0,
            file,
            type,
          });
        }
      }

      toast.success(t("driversCreatePage.toast.updateSuccess"));

      await fetchVehicleInfo();
      await fetchVehicleDocuments();

      if (onVehicleUpdate) {
        onVehicleUpdate();
      }

      setIsEditMode(false);
      setFileUpdates({});
    } catch (error) {
      requestErrorDisplay(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDocumentEdit = (doc) => {
    const input = document.createElement("input");
    input.type = "file";
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        setFileUpdates((prev) => ({ ...prev, [doc.type]: file }));
        toast.info(t("driversCreatePage.toast.fileSelected"));
      }
    };
    input.click();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className={style.modal_overlay} onClick={onClose}>
        <div className={style.modal_container} onClick={(e) => e.stopPropagation()}>
          {isLoading ? (
            <Loading />
          ) : vehicleInfo ? (
            <div className={style.content}>
              <div className={style.field_col_2}>
                <div className={style.field}>
                  <span className={style.field_label}>{t("carsCreatePage.vehicleType.label")} *</span>
                  {isEditMode ? (
                    <SelectCustom
                      placeholder={t("carsCreatePage.vehicleType.placeholder")}
                      options={types}
                      defaultValue={vehicleType}
                      returnObject
                      bgGray
                      onSubmit={(value) => {
                        setVehicleType(value);
                        setVehicleTypeId(value?.id || "");
                      }}
                    />
                  ) : (
                    <div className={style.field_value}>{vehicleInfo.vehicleTypeName || "-"}</div>
                  )}
                </div>
                <div className={style.field}>
                  <span className={style.field_label}>{t("carsCreatePage.loadingType.label")}</span>
                  {isEditMode ? (
                    <SelectCustom
                      placeholder={t("carsCreatePage.loadingType.placeholder")}
                      options={loadingTypes}
                      defaultValue={loadingType}
                      returnObject
                      bgGray
                      onSubmit={(value) => {
                        setLoadingType(value);
                        setLoadingTypeId(value?.id || "");
                      }}
                    />
                  ) : (
                    <div className={style.field_value}>{vehicleInfo.loadingTypeName || "-"}</div>
                  )}
                </div>
              </div>

              <div className={style.field_col_2}>
                <div className={style.field}>
                  <span className={style.field_label}>{t("carsCreatePage.carModel.label")} *</span>
                  {isEditMode ? (
                    <InputCustom
                      placeholder={t("carsCreatePage.carModel.placeholder")}
                      defaultValue={carModel}
                      onChangeValue={(value) => {
                        setCarModel(value);
                        if (errors.carModel) setErrors((prev) => ({ ...prev, carModel: "" }));
                      }}
                      className={errors.carModel ? style.input_error : ""}
                      errorText={errors?.carModel}
                    />
                  ) : (
                    <div className={style.field_value}>{vehicleInfo.carModel || "-"}</div>
                  )}
                </div>
                <div className={style.field}>
                  <span className={style.field_label}>{t("carsCreatePage.carNumber.label")} *</span>
                  {isEditMode ? (
                    <InputCustom
                      placeholder={t("carsCreatePage.carNumber.placeholder")}
                      defaultValue={carNumber}
                      onChangeValue={(value) => {
                        setCarNumber(value);
                        if (errors.carNumber) setErrors((prev) => ({ ...prev, carNumber: "" }));
                      }}
                      className={errors.carNumber ? style.input_error : ""}
                      errorText={errors?.carNumber}
                    />
                  ) : (
                    <div className={style.field_value}>{vehicleInfo.carNumber || "-"}</div>
                  )}
                </div>
              </div>

              <div className={style.field_col_2}>
                <div className={style.field}>
                  <span className={style.field_label}>{t("carsCreatePage.trailerModel.label")} *</span>
                  {isEditMode ? (
                    <InputCustom
                      placeholder={t("carsCreatePage.trailerModel.placeholder")}
                      defaultValue={trailerModel}
                      onChangeValue={(value) => {
                        setTrailerModel(value);
                        if (errors.trailerModel) setErrors((prev) => ({ ...prev, trailerModel: "" }));
                      }}
                      className={errors.trailerModel ? style.input_error : ""}
                      errorText={errors?.trailerModel}
                    />
                  ) : (
                    <div className={style.field_value}>{vehicleInfo.trailerModel || "-"}</div>
                  )}
                </div>
                <div className={style.field}>
                  <span className={style.field_label}>{t("carsCreatePage.trailerNumber.label")} *</span>
                  {isEditMode ? (
                    <InputCustom
                      placeholder={t("carsCreatePage.trailerNumber.placeholder")}
                      defaultValue={trailerNumber}
                      onChangeValue={(value) => {
                        setTrailerNumber(value);
                        if (errors.trailerNumber) setErrors((prev) => ({ ...prev, trailerNumber: "" }));
                      }}
                      className={errors.trailerNumber ? style.input_error : ""}
                      errorText={errors?.trailerNumber}
                    />
                  ) : (
                    <div className={style.field_value}>{vehicleInfo.trailerNumber || "-"}</div>
                  )}
                </div>
              </div>

              {documents && documents.length > 0 && (
                <div>
                  <h4 className={style.field_section}>{t("orderInfo.driverDocumentViewer.title")}</h4>

                  <div className={style.documentsList}>
                    {documents.map((doc, index) => (
                      <div key={index} className={style.documentItem}>
                        <div className={style.documentItem_icon}>
                          <DocumentNewIcon />
                        </div>
                        <div className={style.documentItem_content}>
                          <div className={style.documentItem_name}>
                            {doc?.typeName}.{formatDocumentType(doc?.contentType)}
                          </div>
                          <div className={style.documentItem_size}>{doc?.sideName}</div>
                        </div>

                        {isEditMode && (
                          <SquarePen
                            color="#6D7885"
                            size={22}
                            style={{ cursor: "pointer" }}
                            onClick={() => handleDocumentEdit(doc)}
                          />
                        )}

                        {loadingStatesDownload[doc?.fileId] ? (
                          <Loading inBlock marginBottom={0} />
                        ) : (
                          <UnloadIcon onClick={() => onDownload(doc.fileId)} style={{ cursor: "pointer" }} />
                        )}

                        {loadingStates[doc?.fileId] ? (
                          <Loading inBlock marginBottom={0} />
                        ) : (
                          <EyeIcon onClick={() => openViewPhoto(doc?.fileId)} style={{ cursor: "pointer" }} />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {isEditMode && (
                <div className={style.button_container}>
                  <button className={style.buttonCancel} onClick={handleEditToggle} disabled={isSaving}>
                    {t("driversCreatePage.buttons.cancel") || "Отмена"}
                  </button>
                  <button
                    className={cn(style.button_upload, {
                      [style.button_upload_loading]: isSaving,
                    })}
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {!isSaving && <span>{t("common.save")}</span>}
                    {isSaving && <Loading inBlock marginBottom={0} />}
                  </button>
                </div>
              )}
            </div>
          ) : null}

          {!isEditMode && (
            <button
              onClick={handleEditToggle}
              className={style.buttonEdit}
              title={t("driversCreatePage.buttons.edit") || "Редактировать"}
            >
              <SquarePen size={20} />
            </button>
          )}

          <button onClick={onClose} className={style.buttonClose}>
            <CloseIcon />
          </button>
        </div>
      </div>

      {isPhotoViewerOpen && (
        <PhotoViewer
          imgUrl={imgUrl}
          onClose={handlePhotoViewerClose}
          onDownload={() => onDownload(currentDocType)}
          ShowBtnDownload
        />
      )}
    </>
  );
};

export default VehicleInfoModal;
